import React, { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';

const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;
const CENTER = {
  lat: Number(process.env.REACT_APP_MAP_CENTER_LAT) || 13.6549,
  lng: Number(process.env.REACT_APP_MAP_CENTER_LNG) || 78.9471,
};

function loadMaps() {
  if (window.google?.maps) return Promise.resolve();
  if (!MAPS_KEY) return Promise.reject(new Error('missing_maps_key'));
  return new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-mt-maps]');
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', reject);
      return;
    }
    const s = document.createElement('script');
    s.src = `https://maps.googleapis.com/maps/api/js?key=${MAPS_KEY}`;
    s.async = true;
    s.dataset.mtMaps = '1';
    s.onload = () => resolve();
    s.onerror = reject;
    document.head.appendChild(s);
  });
}

const PILER_HUBS = [
  { name: 'APSRTC Bus Stand', lat: 13.6547389, lng: 78.9445406 },
  { name: 'Piler Cross Road', lat: 13.654869, lng: 78.9471378 },
  { name: 'Market Yard Complex', lat: 13.6563587, lng: 78.9454413 },
  { name: 'Railway Station', lat: 13.6625162, lng: 78.9280908 },
  { name: 'Govt Hospital', lat: 13.654472, lng: 78.9418376 },
  { name: 'Bodumalluvaripalle Junction', lat: 13.6601, lng: 78.9610 },
  { name: 'VSR Convention', lat: 13.6480, lng: 78.9390 },
];

export default function DemandHeatmap({ rides = [] }) {
  const mapRef = useRef(null);
  const mapInst = useRef(null);
  const circlesRef = useRef([]);
  const [filterType, setFilterType] = useState('missed'); // 'all' | 'missed' | 'fulfilled'
  const [timeFilter, setTimeFilter] = useState('7days'); // 'today' | '7days' | 'all'
  const [selectedRide, setSelectedRide] = useState(null);
  const [mapType, setMapType] = useState('roadmap');
  const [ready, setReady] = useState(false);

  // Filter rides based on selection
  const filteredRides = useMemo(() => {
    const now = Date.now();
    return rides.filter((r) => {
      // Time filter
      const rideTime = r.createdAt?.toDate ? r.createdAt.toDate().getTime() : (r.createdAt ? new Date(r.createdAt).getTime() : now);
      if (timeFilter === 'today' && now - rideTime > 24 * 60 * 60 * 1000) return false;
      if (timeFilter === '7days' && now - rideTime > 7 * 24 * 60 * 60 * 1000) return false;

      // Status filter
      const isMissed = ['cancelled', 'expired', 'no_anna', 'searching'].includes(r.status);
      const isFulfilled = ['completed', 'started', 'arrived', 'driver_assigned'].includes(r.status);

      if (filterType === 'missed') return isMissed;
      if (filterType === 'fulfilled') return isFulfilled;
      return true;
    });
  }, [rides, filterType, timeFilter]);

  // Aggregate Metrics & Hotspots
  const metrics = useMemo(() => {
    let totalSearches = 0;
    let missedCount = 0;
    let fulfilledCount = 0;
    let missedLostRevenue = 0;
    const locationCounts = {};

    const now = Date.now();
    rides.forEach((r) => {
      const rideTime = r.createdAt?.toDate ? r.createdAt.toDate().getTime() : (r.createdAt ? new Date(r.createdAt).getTime() : now);
      if (timeFilter === 'today' && now - rideTime > 24 * 60 * 60 * 1000) return;
      if (timeFilter === '7days' && now - rideTime > 7 * 24 * 60 * 60 * 1000) return;

      totalSearches += 1;
      const isMissed = ['cancelled', 'expired', 'no_anna', 'searching'].includes(r.status);
      if (isMissed) {
        missedCount += 1;
        missedLostRevenue += Number(r.fare || 40);

        const locName = r.pickup?.name || r.pickup?.address || 'Piler Town Center';
        const cleanName = locName.split(',')[0].trim();
        locationCounts[cleanName] = (locationCounts[cleanName] || 0) + 1;
      } else {
        fulfilledCount += 1;
      }
    });

    const topZones = Object.entries(locationCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 4);

    const fulfillmentRate = totalSearches > 0 ? Math.round((fulfilledCount / totalSearches) * 100) : 100;

    return {
      totalSearches,
      missedCount,
      fulfilledCount,
      fulfillmentRate,
      missedLostRevenue,
      topZones,
    };
  }, [rides, timeFilter]);

  // Initialize Map
  useEffect(() => {
    let cancelled = false;
    loadMaps()
      .then(() => {
        if (cancelled || !mapRef.current || mapInst.current) return;
        mapInst.current = new window.google.maps.Map(mapRef.current, {
          center: CENTER,
          zoom: 14,
          mapTypeId: mapType,
          disableDefaultUI: false,
          zoomControl: true,
          streetViewControl: false,
          mapTypeControl: false,
          styles: [
            { elementType: 'geometry', stylers: [{ color: '#131b26' }] },
            { elementType: 'labels.text.fill', stylers: [{ color: '#9baec8' }] },
            { elementType: 'labels.text.stroke', stylers: [{ color: '#131b26' }] },
            { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#27384c' }] },
            { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#3b5370' }] },
            { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#090e14' }] },
          ],
        });
        setReady(true);
      })
      .catch((e) => console.error('Maps error:', e));
    return () => { cancelled = true; };
  }, []);

  // Update map type
  useEffect(() => {
    if (mapInst.current) {
      mapInst.current.setMapTypeId(mapType);
    }
  }, [mapType]);

  // Draw Heatmap circles for Demand
  useEffect(() => {
    if (!ready || !mapInst.current || !window.google?.maps) return;

    // Clear old circles
    circlesRef.current.forEach((c) => c.setMap(null));
    circlesRef.current = [];

    filteredRides.forEach((ride) => {
      const lat = ride.pickup?.lat || ride.pickupLocation?.lat;
      const lng = ride.pickup?.lng || ride.pickupLocation?.lng;
      if (!lat || !lng) return;

      const isMissed = ['cancelled', 'expired', 'no_anna', 'searching'].includes(ride.status);
      const color = isMissed ? '#ef4444' : '#10b981'; // Red for missed, green for fulfilled

      // Glowing outer heat circle
      const circle = new window.google.maps.Circle({
        strokeColor: color,
        strokeOpacity: 0.85,
        strokeWeight: 2,
        fillColor: color,
        fillOpacity: isMissed ? 0.35 : 0.2,
        map: mapInst.current,
        center: { lat: Number(lat), lng: Number(lng) },
        radius: isMissed ? 120 : 80,
      });

      circle.addListener('click', () => {
        setSelectedRide(ride);
      });

      circlesRef.current.push(circle);
    });
  }, [ready, filteredRides]);

  // Copy WhatsApp driver broadcast message
  const handleCopyWhatsAppBroadcast = () => {
    const topSpots = metrics.topZones.length > 0
      ? metrics.topZones.map((z) => `📍 *${z[0]}* (${z[1]} రైడ్లు)`).join('\n')
      : '📍 *APSRTC బస్టాండ్ & రైల్వే స్టేషన్*';

    const msg = `📢 *MyTownly Rides - అన్నలందరికీ ముఖ్య సమాచారం!* 📢\n\nఈరోజు పీలేరులో కింది ప్రాంతాల్లో ప్రయాణికులు ఆటోల కోసం చాలా వెతుకుతున్నారు:\n\n${topSpots}\n\nదయచేసి ఈ ప్రదేశాల దగ్గర ఆటోలు పెట్టి *ఆన్‌లైన్* రండి. మంచి సంపాదన పొందండి! 🛺💰\n\n- MyTownly Piler టీమ్`;

    navigator.clipboard.writeText(msg).then(() => {
      toast.success('WhatsApp announcement copied! Ready to paste into driver group.', { duration: 4000 });
    }).catch(() => {
      toast.error('Failed to copy. Check clipboard permissions.');
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 120px)', gap: 16 }}>
      {/* Top Demand Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 }}>
        <div style={{ background: '#1e293b', padding: '14px 18px', borderRadius: 12, border: '1px solid #334155' }}>
          <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600, display: 'block' }}>🔍 Passenger Ride Searches</span>
          <strong style={{ fontSize: 24, color: '#f8fafc', fontWeight: 800 }}>{metrics.totalSearches}</strong>
          <small style={{ color: '#38bdf8', marginLeft: 8 }}>Total Demands</small>
        </div>

        <div style={{ background: '#1e293b', padding: '14px 18px', borderRadius: 12, border: '1px solid #334155' }}>
          <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600, display: 'block' }}>❌ Missed / Unserved Searches</span>
          <strong style={{ fontSize: 24, color: '#ef4444', fontWeight: 800 }}>{metrics.missedCount}</strong>
          <small style={{ color: '#f87171', marginLeft: 8 }}>~₹{metrics.missedLostRevenue} Lost Gross</small>
        </div>

        <div style={{ background: '#1e293b', padding: '14px 18px', borderRadius: 12, border: '1px solid #334155' }}>
          <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600, display: 'block' }}>📊 Fulfillment Rate</span>
          <strong style={{ fontSize: 24, color: metrics.fulfillmentRate >= 80 ? '#10b981' : '#f59e0b', fontWeight: 800 }}>
            {metrics.fulfillmentRate}%
          </strong>
          <small style={{ color: '#94a3b8', marginLeft: 8 }}>{metrics.fulfilledCount} Completed</small>
        </div>

        <div style={{ background: '#1e293b', padding: '14px 18px', borderRadius: 12, border: '1px solid #334155' }}>
          <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600, display: 'block' }}>🔥 Top Hotspot</span>
          <strong style={{ fontSize: 18, color: '#f8fafc', fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>
            {metrics.topZones[0] ? metrics.topZones[0][0] : 'Piler Bus Stand'}
          </strong>
          <small style={{ color: '#fbbf24' }}>
            {metrics.topZones[0] ? `${metrics.topZones[0][1]} missed requests` : 'High demand'}
          </small>
        </div>
      </div>

      {/* Control & Broadcast Bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, background: '#1e293b', padding: '10px 16px', borderRadius: 12, border: '1px solid #334155' }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, color: '#94a3b8', fontWeight: 600 }}>Demand View:</span>
          <button
            type="button"
            onClick={() => setFilterType('missed')}
            style={{
              padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer',
              background: filterType === 'missed' ? '#ef4444' : '#334155', color: '#fff', border: 'none',
            }}
          >
            🔥 Missed Demand ({metrics.missedCount})
          </button>
          <button
            type="button"
            onClick={() => setFilterType('fulfilled')}
            style={{
              padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer',
              background: filterType === 'fulfilled' ? '#10b981' : '#334155', color: '#fff', border: 'none',
            }}
          >
            ✅ Fulfilled Rides ({metrics.fulfilledCount})
          </button>
          <button
            type="button"
            onClick={() => setFilterType('all')}
            style={{
              padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer',
              background: filterType === 'all' ? '#0284c7' : '#334155', color: '#fff', border: 'none',
            }}
          >
            All Searches ({metrics.totalSearches})
          </button>

          <span style={{ fontSize: 13, color: '#94a3b8', fontWeight: 600, marginLeft: 10 }}>Time:</span>
          <select
            value={timeFilter}
            onChange={(e) => setTimeFilter(e.target.value)}
            style={{
              background: '#0f172a', color: '#f8fafc', border: '1px solid #475569',
              padding: '6px 10px', borderRadius: 8, fontSize: 12, fontWeight: 600,
            }}
          >
            <option value="today">Today (24h)</option>
            <option value="7days">Last 7 Days</option>
            <option value="all">All Time</option>
          </select>
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            type="button"
            onClick={() => setMapType(mapType === 'roadmap' ? 'hybrid' : 'roadmap')}
            style={{
              padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer',
              background: '#334155', color: '#f8fafc', border: '1px solid #475569',
            }}
          >
            {mapType === 'roadmap' ? '🛰️ Satellite View' : '🗺️ Dark Road Map'}
          </button>

          <button
            type="button"
            onClick={handleCopyWhatsAppBroadcast}
            style={{
              padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 800, cursor: 'pointer',
              background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)', color: '#fff', border: 'none',
              display: 'flex', alignItems: 'center', gap: 6,
            }}
          >
            <span>💬 Copy WhatsApp Broadcast for Annas</span>
          </button>
        </div>
      </div>

      {/* Main Heatmap Container */}
      <div style={{ position: 'relative', flex: 1, minHeight: 400, borderRadius: 14, overflow: 'hidden', border: '1px solid #334155' }}>
        <div ref={mapRef} style={{ width: '100%', height: '100%' }} />

        {/* Selected Ride Inspection Card */}
        {selectedRide && (
          <div style={{
            position: 'absolute', bottom: 20, left: 20, background: 'rgba(15, 23, 42, 0.95)',
            border: '1px solid #38bdf8', padding: '14px 18px', borderRadius: 12, color: '#fff',
            maxWidth: 340, backdropFilter: 'blur(8px)', zIndex: 10,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <strong style={{ fontSize: 14, color: '#38bdf8' }}>Demand Search Details</strong>
              <button
                type="button"
                onClick={() => setSelectedRide(null)}
                style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 16 }}
              >
                ✕
              </button>
            </div>
            <div style={{ fontSize: 12, color: '#cbd5e1', display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div>📍 <strong>Pickup:</strong> {selectedRide.pickup?.address || selectedRide.pickup?.name || 'Piler'}</div>
              <div>🎯 <strong>Drop:</strong> {selectedRide.destination?.address || selectedRide.destination?.name || 'Destination'}</div>
              <div>💰 <strong>Est. Fare:</strong> ₹{selectedRide.fare || 40}</div>
              <div>⚡ <strong>Status:</strong> <span style={{
                color: ['cancelled', 'expired', 'no_anna', 'searching'].includes(selectedRide.status) ? '#ef4444' : '#10b981',
                fontWeight: 700, textTransform: 'uppercase',
              }}>{selectedRide.status}</span></div>
              {selectedRide.cancelReason && <div>ℹ️ <strong>Reason:</strong> {selectedRide.cancelReason}</div>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
